import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { buildProductsCacheKey, CACHE_CONFIG, getValidCacheData } from "@/utils/cacheManager";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { shop_per_page } from "@/viewModels/shop/constants";
import type { ProductsQuery } from "@/viewModels/shop/features/useFilter/types";
import type { Product } from "@/types/products";
import type { RootState } from "@/store/store";
import type { CacheItem } from "@/store/cacheReducer";

interface ProductListCacheData {
  data: Product[];
  total: number;
}

interface ProductListViewModel {
  products: Product[];
  hasMoreProducts: boolean;
  isLoadingProducts: boolean;
  fetchProducts: (signal?: AbortSignal) => void;
  fetchMoreProducts: () => void;
}

const useProductList = ({ filter }: { filter: ProductsQuery }): ProductListViewModel => {
  const appDispatch = useAppDispatch();

  const cacheState = useSelector((store: RootState) => store.cache);
  const cacheKey = useMemo(() => buildProductsCacheKey(filter), [filter]);
  // 讀取快取資料
  const cachedData = useMemo(
    () => getValidCacheData<ProductListCacheData>(cacheKey, cacheState),
    [cacheKey, cacheState],
  );

  // 從快取取得商品列表
  const products = useMemo(() => cachedData?.data ?? [], [cachedData]);

  const [isLoading, setIsLoading] = useState(false);

  // 初始化 refs
  const queryPageRef = useRef(1);
  const totalProductsRef = useRef(-1); // 初始化為 -1 表示尚未設定總商品數

  const hasMoreProducts = useMemo(
    () => products.length < totalProductsRef.current,
    [products, totalProductsRef.current],
  );

  const fetchProducts = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const params = {
        ...filter,
        page: 1,
      };

      const response = await wooStoreApiService.product.getList(params, signal);
      const { data, headers } = response;
      const total = parseInt(headers["x-wp-total"] || "0", 10);
      totalProductsRef.current = total;
      queryPageRef.current = 2;

      if (signal?.aborted) return;
      const cacheItem: CacheItem = {
        data: {
          data,
          total,
        },
        expiry: CACHE_CONFIG.products.expiry,
        lastFetched: Date.now(),
      };

      appDispatch.setCache(cacheKey, cacheItem);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "取得商品發生錯誤，請重新整理頁面再試一次");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  const fetchMoreProducts = async () => {
    if (isLoading || (totalProductsRef.current > 0 && products.length >= totalProductsRef.current))
      return; // 防止重複請求或超出總商品數
    setIsLoading(true);

    try {
      const params = {
        ...filter,
        page: queryPageRef.current,
      };

      const response = await wooStoreApiService.product.getList(params);
      const { data, headers } = response;
      const total = parseInt(headers["x-wp-total"] || "0", 10);
      totalProductsRef.current = total;
      queryPageRef.current = queryPageRef.current + 1;

      // 累積到現有快取
      const cacheItem: CacheItem = {
        data: {
          data: [...products, ...data],
          total,
        },
        expiry: CACHE_CONFIG.products.expiry,
        lastFetched: Date.now(),
      };

      appDispatch.setCache(cacheKey, cacheItem);
    } catch (error) {
      handleApiError(error, appDispatch, "載入更多商品失敗，請稍後再試");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController(); // 快速切換分類時，取消前一次請求

    if (!!cachedData?.data.length) return;
    // 重置分頁
    queryPageRef.current = 1;
    totalProductsRef.current = -1;
    fetchProducts(controller.signal);

    return () => {
      controller.abort();
    };
  }, [filter]);

  // 同步 refs 的值（當快取資料變更時）
  useEffect(() => {
    if (!cachedData) return;
    queryPageRef.current = Math.ceil(products.length / shop_per_page) + 1;
    totalProductsRef.current = cachedData.total;
  }, [cachedData]);

  return {
    products,
    hasMoreProducts,
    isLoadingProducts: isLoading,
    fetchProducts,
    fetchMoreProducts,
  };
};

export default useProductList;
